// embedCompatMethods — compat method-set for the legacy `system/platform`
// project whose pages render inside canvas embed tiles (CanvasItem).
//
// Those pages were authored against the platform app-shell runtime
// (`~/www/next/platform-legacy/uikit/methods/*`) and call project-scoped
// element methods directly — `el.getDesignSystem()`, `el.getData()`,
// `el.isCanvas()`, … The embed runtime (`createIframeApp`) never registered
// those methods, so every reference threw `… is not a function`, caught
// per-element by `if:` / effect guards but flooding the console.
//
// These implementations are COPIED from platform-legacy/uikit/methods (root.js,
// db.js, design-system.js, project.js, canvas.js) and ADAPTED for the embed:
// the platform versions read the platform app-shell state
// (`window.platformState`, `root.key === 'platform'`, `DEFAULTS`, schema, …)
// which does NOT exist inside an embed iframe — a verbatim copy would throw on
// null destructure. So every base accessor here returns an empty shape instead
// of throwing, and the leaf methods degrade to `undefined`/`{}` so the platform
// pages' `if:` guards simply read false and skip those subtrees.
//
// Registered as the LOWEST-priority method layer in createIframeApp
// (`{ ...embedCompatMethods, ...project.methods, ...contextOverrides.methods }`)
// so a project's own method of the same name always wins.

// Safe root-state read. v3.14 exposes `getRootState` on the element proto; the
// embed root state is the project's `state`. Never throws.
// USER-CODE-ORIGIN: ONE self-contained factory. The opaque user-code frame
// (userCodeFrame.js) cannot share objects with the parent, so its bootstrap
// carries this factory as TEXT (`makeEmbedCompatMethods.toString()`) and calls
// it inside the frame. Everything it needs therefore lives INSIDE the function:
// no module-scope helper, no import, and ES2017 syntax only (no object spread,
// no optional catch binding) so no bundler helper is referenced by the text.
// The parent-realm export below is the same factory, called once.
export function makeEmbedCompatMethods() {
  // Safe root-state read. v3.14 exposes `getRootState` on the element proto;
  // the embed root state is the project's `state`. Never throws.
  function _rootState(self, key) {
    var state
    try {
      state = self.getRootState && self.getRootState()
    } catch (e) {
      state = null
    }
    state = state || {}
    return key ? state[key] : state
  }

  // Mirror platform `getData`'s shape, sourced from the embed context (where
  // designSystem/components/etc. actually live) + root state. Every field is
  // an object so downstream `key in value` / `value[key]` reads never throw.
  function _data(self, key) {
    var ctx = (self && self.context) || {}
    var rs = _rootState(self)
    var data = {
      designSystem: ctx.designSystem || {},
      state: rs || {},
      components: ctx.components || {},
      snippets: ctx.snippets || {},
      pages: ctx.pages || {},
      functions: ctx.functions || {},
      methods: ctx.methods || {},
      files: ctx.files || rs.files || {},
      dependencies: ctx.dependencies || rs.dependencies || {}
    }
    return key ? data[key] || {} : data
  }

  var _noop = function () {
    return undefined
  }
  var _falseFn = function () {
    return false
  }
  // The realtime event bus the legacy pages subscribe to
  // (`el.sdk.rootBus.on(…)`, `.emit(…)`) — no-op in the embed (no collab
  // session).
  var _embedBus = { on: _noop, off: _noop, once: _noop, emit: _noop }
  // Permission/state predicates the legacy pages call INSIDE `if:` guards —
  // must return a FALSY value so the guarded editor-only subtree is skipped in
  // the read-only embed, rather than throwing `el.sdk.<x> is not a function`.
  var _SDK_FALSE = ['hasPermission', 'isAuthorised', 'isAuthorized', 'isOwner', 'can', 'isReady']

  // Embed SDK shim (2026-06-16, workspace.md "Embed runtime parity" sub-item:
  // `el.sdk` undefined inside `if:` guards). The legacy `system/platform`
  // pages read `el.sdk.*` directly — permission guards
  // (`el.sdk.hasPermission('iam')`), the realtime `rootBus`,
  // undo/redo/checkpoint, getProjectByKey, … The embed has no SDK, so
  // `el.sdk` was undefined and every access threw (caught by the pages'
  // `if:`/effect guards but flooding the console). smbls exposes a
  // NON-function `context.methods` entry as the shared `el.sdk` PROPERTY
  // (element/src/methods.js getContextProto), so this Proxy degrades every
  // access safely: permission checks read false, `rootBus.on/emit` no-op,
  // every other method returns undefined (await-safe). `then` is undefined so
  // the proxy is never mistaken for a thenable (which would hang an
  // accidental `await`).
  var _embedSdkStub = new Proxy(
    {},
    {
      get: function (_t, prop) {
        if (prop === 'rootBus') return _embedBus
        if (prop === 'then' || typeof prop === 'symbol') return undefined
        if (_SDK_FALSE.indexOf(prop) !== -1) return _falseFn
        return _noop
      }
    }
  )

  return {
    // Shared `el.sdk` property (non-function → exposed as a property, not a
    // method). A project's own `sdk` in project.methods still wins.
    sdk: _embedSdkStub,

    // --- root.js ---
    getData: function (key) {
      return _data(this, key)
    },
    getAppKey: function () {
      return _rootState(this, 'appKey')
    },
    getSecrets: function (key) {
      var secrets = _rootState(this, 'secrets') || {}
      return key ? secrets[key] : secrets
    },
    getDependencies: function (key) {
      var dependencies = _data(this, 'dependencies')
      return key ? dependencies[key] : dependencies
    },
    getPackages: function (key) {
      var schema = _rootState(this, 'schema') || {}
      var packages = schema.dependencies || {}
      return key ? packages[key] : packages
    },
    getUserSettings: function (key) {
      var userSettings = _rootState(this, 'user') || {}
      return key ? userSettings[key] : userSettings
    },
    getFiles: function (key) {
      var files = _data(this, 'files')
      return key ? files[key] : files
    },

    // --- design-system.js ---
    // Read the prepared design system off the embed context (scratch CONFIG),
    // tolerating both lowercase (canonical) and UPPERCASE (legacy) category keys.
    getDesignSystem: function (type) {
      var ds = (this.context && this.context.designSystem) || {}
      if (type) {
        var lower = ds[type.toLowerCase()]
        var upper = ds[type.toUpperCase()]
        return Object.assign({}, upper || {}, lower || {})
      }
      return ds
    },

    // --- project.js ---
    getActiveBranch: function () {
      return _rootState(this, 'branch') || 'main'
    },

    // --- db.js ---
    // Defensive embed port: find an item's value across the project data shape;
    // return its `{ value }` wrapper, or undefined when absent (no platform
    // schema in the embed). Iterates only object values so `key in v` is safe.
    getItem: function (key) {
      if (!key) return
      var data = _data(this)
      for (var cat in data) {
        var bucket = data[cat]
        if (bucket && typeof bucket === 'object' && key in bucket) {
          return { value: bucket[key] }
        }
      }
    },

    // --- root.js getGlobalTheme ---
    // The theme the embedded app runs in. The platform version read the
    // EDITOR's `sceneTheme` off the platform root state; inside an embed the
    // answer is the app's own resolved theme: a root-state `sceneTheme` if the
    // project keeps one, else the `data-theme` the framework stamped on the
    // app root, else the configured `globalTheme`.
    getGlobalTheme: function () {
      var rs = _rootState(this)
      if (rs && rs.sceneTheme) return rs.sceneTheme
      var ctx = this.context || {}
      var doc = ctx.document || (typeof document !== 'undefined' ? document : null)
      var stamped = doc && doc.documentElement && doc.documentElement.getAttribute('data-theme')
      if (stamped) return stamped
      return (ctx.designSystem && ctx.designSystem.globalTheme) || 'light'
    },

    // --- canvas.js ---
    // Inside an embed iframe the app is, by definition, an embed — not the
    // platform canvas editor and not the dashboard. Static values; no window
    // location parsing (which the platform versions used against the editor URL).
    isEmbed: function () {
      return true
    },
    isCanvas: function () {
      return false
    },
    isDashboard: function () {
      return false
    }
  }
}

export const embedCompatMethods = makeEmbedCompatMethods()

export default embedCompatMethods
